import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Plus, Star, Trash2, Edit, LogOut, Home, Building, Warehouse } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

function MyAddresses({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAddress, setEditingAddress] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    label: '',
    address_line: '',
    city: 'Алматы',
    district: '',
    postal_code: '',
    phone: '',
    contact_person: '',
    notes: '',
    is_default: false
  });

  const labelPresets = [
    { value: 'Дом', icon: Home },
    { value: 'Офис', icon: Building },
    { value: 'Склад', icon: Warehouse },
  ];

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, { headers: getAuthHeaders() });
      setAddresses(response.data);
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAddress) {
        await axios.put(`${API}/addresses/${editingAddress.id}`, formData, { headers: getAuthHeaders() });
        toast.success(t('addresses.updated') || 'Адрес обновлён');
      } else {
        await axios.post(`${API}/addresses`, formData, { headers: getAuthHeaders() });
        toast.success(t('addresses.added') || 'Адрес добавлен');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchAddresses();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.failed') || 'Ошибка сохранения');
    }
  };

  const handleDelete = async (addressId) => {
    if (!window.confirm(t('addresses.confirmDelete') || 'Удалить этот адрес?')) return;
    
    try {
      await axios.delete(`${API}/addresses/${addressId}`, { headers: getAuthHeaders() });
      toast.success(t('addresses.deleted') || 'Адрес удалён');
      fetchAddresses();
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка удаления');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      await axios.put(`${API}/addresses/${addressId}/set-default`, {}, { headers: getAuthHeaders() });
      toast.success(t('addresses.defaultSet') || 'Адрес по умолчанию установлен');
      fetchAddresses();
    } catch (error) {
      toast.error(t('admin.failed') || 'Ошибка');
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label || '',
      address_line: address.address_line || '',
      city: address.city || 'Алматы',
      district: address.district || '',
      postal_code: address.postal_code || '',
      phone: address.phone || '',
      contact_person: address.contact_person || '',
      notes: address.notes || '',
      is_default: address.is_default || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAddress(null);
    setFormData({
      label: '',
      address_line: '',
      city: 'Алматы',
      district: '',
      postal_code: '',
      phone: '',
      contact_person: '',
      notes: '',
      is_default: false
    });
  };

  const handleOpenNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getLabelIcon = (label) => {
    const preset = labelPresets.find(p => p.value === label);
    return preset ? preset.icon : MapPin;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.backToCatalog')}
        </Link>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Link to={`/${currentLang}/profile`}>
            <Button variant="outline">{t('profile.title') || 'Профиль'}</Button>
          </Link>
          <Link to={`/${currentLang}/settings`}>
            <Button variant="outline">{t('settings.title') || 'Настройки'}</Button>
          </Link>
          <Link to={`/${currentLang}/orders`}>
            <Button variant="outline">{t('common.myOrders')}</Button>
          </Link>
          <Link to={`/${currentLang}/addresses`}>
            <Button variant="default" className="bg-emerald-700 hover:bg-emerald-800">
              {t('common.myAddresses')}
            </Button>
          </Link>
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/legal-entities`}>
              <Button variant="outline">{t('legalEntities.title') || 'Юрлица'}</Button>
            </Link>
          )}
          {user?.role === 'B2B' && (
            <Link to={`/${currentLang}/b2b/networks`}>
              <Button variant="outline">Мои сети</Button>
            </Link>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <CardTitle>{t('addresses.title') || 'Адреса доставки'}</CardTitle>
                  <CardDescription>{t('addresses.subtitle') || 'Управляйте адресами доставки'}</CardDescription>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={handleOpenNew} className="bg-emerald-700 hover:bg-emerald-800">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addresses.add') || 'Добавить'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingAddress ? (t('addresses.edit') || 'Редактировать адрес') : (t('addresses.addNew') || 'Новый адрес')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('addresses.fillFields') || 'Заполните информацию об адресе доставки'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Label with presets */}
                    <div>
                      <Label>{t('addresses.label') || 'Название'}</Label>
                      <div className="flex gap-2 mt-1 mb-2">
                        {labelPresets.map(preset => {
                          const Icon = preset.icon;
                          return (
                            <button
                              key={preset.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, label: preset.value })}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                formData.label === preset.value 
                                  ? 'bg-emerald-100 border-emerald-500 text-emerald-700' 
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {preset.value}
                            </button>
                          );
                        })}
                      </div>
                      <Input
                        value={formData.label}
                        onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                        placeholder={t('addresses.labelPlaceholder') || 'Или введите своё название'}
                      />
                    </div>

                    {/* City */}
                    <div>
                      <Label>{t('addresses.city') || 'Город'} *</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        placeholder="Алматы"
                        className="mt-1"
                      />
                    </div>

                    {/* Address Line */}
                    <div>
                      <Label>{t('addresses.addressLine') || 'Улица, дом, квартира'} *</Label>
                      <Input
                        value={formData.address_line}
                        onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
                        required
                        placeholder="ул. Абая 1, кв. 10"
                        className="mt-1"
                      />
                    </div>

                    {/* District */}
                    <div>
                      <Label>{t('addresses.district') || 'Район'}</Label>
                      <Input
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        placeholder="Алмалинский район"
                        className="mt-1"
                      />
                    </div>

                    {/* Contact Person & Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('addresses.contactPerson') || 'Контактное лицо'}</Label>
                        <Input
                          value={formData.contact_person}
                          onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                          placeholder="Иван Иванов"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>{t('addresses.phone') || 'Телефон'} *</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          placeholder="+7 777 123 4567"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {/* Notes for courier */}
                    <div>
                      <Label>{t('addresses.notes') || 'Комментарий для курьера'}</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t('addresses.notesPlaceholder') || 'Домофон не работает, звонить заранее...'}
                        className="mt-1"
                        rows={2}
                      />
                    </div>

                    {/* Default checkbox */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_default"
                        checked={formData.is_default}
                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor="is_default" className="cursor-pointer">
                        {t('addresses.setDefault') || 'Использовать по умолчанию'}
                      </Label>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                        {t('common.cancel') || 'Отмена'}
                      </Button>
                      <Button type="submit" className="flex-1 bg-emerald-700 hover:bg-emerald-800">
                        {editingAddress ? (t('common.save') || 'Сохранить') : (t('addresses.add') || 'Добавить')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {addresses.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">{t('addresses.empty') || 'У вас пока нет сохранённых адресов'}</p>
                <Button onClick={handleOpenNew} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('addresses.addFirst') || 'Добавить первый адрес'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map(address => {
                  const Icon = getLabelIcon(address.label);
                  return (
                    <div 
                      key={address.id} 
                      className={`p-4 rounded-lg border ${address.is_default ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${address.is_default ? 'bg-emerald-200' : 'bg-gray-100'}`}>
                            <Icon className={`w-5 h-5 ${address.is_default ? 'text-emerald-700' : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{address.label || 'Адрес'}</span>
                              {address.is_default && (
                                <Badge className="bg-emerald-600 text-xs">
                                  <Star className="w-3 h-3 mr-1" />
                                  {t('addresses.default') || 'По умолчанию'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-gray-600 mt-1">
                              {address.city}, {address.address_line}
                              {address.district && `, ${address.district}`}
                            </p>
                            {address.contact_person && (
                              <p className="text-sm text-gray-500 mt-1">
                                {address.contact_person} • {address.phone}
                              </p>
                            )}
                            {!address.contact_person && address.phone && (
                              <p className="text-sm text-gray-500 mt-1">{address.phone}</p>
                            )}
                            {address.notes && (
                              <p className="text-xs text-gray-400 mt-1 italic">"{address.notes}"</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!address.is_default && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleSetDefault(address.id)}
                              title={t('addresses.makeDefault') || 'Сделать по умолчанию'}
                            >
                              <Star className="w-4 h-4 text-gray-400" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(address)}
                          >
                            <Edit className="w-4 h-4 text-gray-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(address.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default MyAddresses;
